<template>
	<div v-if="virtualLog">
		<div class="active-virtuallog q-pt-sm q-px-md row justify-between">
			<span class="cursor-pointer text-primary" @click="show()">{{ $t('virtual.title') }} {{ virtualLog.name }}<q-icon name="expand_less"/></span>
			<span v-if="virtualLog.species">{{ virtualLog.species.name }}</span>
			<span v-if="virtualLog.logsMeta">{{ $t('label.itemCount') }}: {{ virtualLog.logsMeta.length }}</span>
			<span v-if="virtualLog.logArea">{{ virtualLog.logArea | area }}</span>
			<span><q-btn @click="close" icon="close" size="sm" flat/></span>
		</div>
		<q-dialog :value="editMode"
				  class="virtual-log-builder bg-white"
				  :full-width="true"
				  square
				  persistent
		>
			<q-card>
				<q-card-section class="bg-primary q-px-md q-py-md q-mb-md">
					<div class="row">
						<div class="col-10">
							<h5 @click="hide" class="text-white q-my-none cursor-pointer">{{ $t('virtual.title') }} {{ virtualLog.name }}</h5>
						</div>
						<div class="col-2">
							<q-btn v-if="!editMode" icon="close" @click="close" color="white" flat/>
						</div>
					</div>
				</q-card-section>

				<template v-if="editMode">
					<q-card-section>
						<template v-if="!virtualLog.uid">
							<log-property-form
									:log="virtualLog"
									:errors="logPropertyFormErrors"
									:show-submit="true"
									:loading="updating"
									@update="updateLogProps"
									@close="close"
									@update-log="updateLog"
							/>
						</template>
						<template v-else>
							<q-spinner-cube v-if="loading" color="primary" size="2em"/>
							<template v-else-if="!virtualLog.logs || (virtualLog.logs && !virtualLog.logs.length)">
								<div class="row q-col-gutter-md q-mt-lg justify-center">
									<div>
										<q-btn @click="addFromSearch"
											   :label="$t('button.addLogsToVirtualFromSearch')"
											   color="accent"
											   text-color="accentText"
											   size="xl"
											   class="large-cta-btn"
											   no-caps
										/>
									</div>
									<div>
										<q-btn @click="addFromWatchlist"
											   :label="$t('button.addLogsToVirtualFromWatchlist')"
											   color="accent"
											   text-color="accentText"
											   size="xl"
											   class="large-cta-btn"
											   no-caps
										/>
									</div>
								</div>
							</template>
							<template v-else>
								<q-btn @click="hide" class="q-mb-md" icon="expand_more" flat>
									<q-tooltip>{{ $t('button.minimize') }}</q-tooltip>
								</q-btn>
								<q-toggle :value="showBundleImages" @input="toggleBundleImages" :label="$t('label.showBundleImages')" :disable="updating"/>

								<q-tabs v-model="tab"
										class="text-black"
										active-color="primary"
										align="left"
										dense
										no-caps
								>
									<q-tab name="bundles" :label="$t('virtual.bundleTab')"/>
									<q-tab name="images" :label="$t('virtual.imageTab')"/>
									<q-tab name="logInfo" :label="$t('virtual.infoTab')"/>
								</q-tabs>

								<q-separator/>

								<q-tab-panels v-model="tab" animated keep-alive>
									<q-tab-panel name="bundles">

										<q-table :columns="logTableColumns"
												 :data="virtualLog.logs"
												 :rows-per-page-options="[5]"
												 rows-per-page-label=""
												 row-key="logId"
												 flat
												 dense
										>
											<template v-slot:body="props">
												<q-tr :props="props" @click="selectLog(props.row)" class="log-selector-tr" :class="{ selected: selectedLog === props.row}">
													<q-td key="logNo" :props="props">{{ props.row.strLogNo || props.row.logNo }}</q-td>
													<q-td key="species" :props="props">{{ props.row.strSpecieName || props.row.species.name }}</q-td>
													<q-td key="actions" :props="props">
														<q-badge :color="logBundleCountColor(props.row)" text-color="black">{{ addedBundles(props.row) }}</q-badge>
														<q-btn @click.stop="addAllBundles(props.row)"
															   icon="add"
															   :loading="loadingBundles"
															   flat
													  	>
															<q-tooltip>{{ $t('virtual.addAllBundles') }}</q-tooltip>
														</q-btn>
														<q-btn @click.stop="removeLog(props.row)"
															   icon="delete"
															   :loading="removingLog"
															   flat
														>
															<q-tooltip>{{ $t('virtual.removeLog') }}</q-tooltip>
														</q-btn>
													</q-td>
												</q-tr>
												<q-tr v-if="isSelected(props.row)" :props="props">
													<q-td colspan="100%">
														<q-spinner-cube v-if="loadingBundles" color="primary" size="2em"/>

														<q-table v-else
																 :columns="bundleTableColumns"
																 :data="props.row.bundles"
																 :pagination.sync="bundleTablePagination"
																 @row-click="bundleTableRowClick"
																 row-key="intBundleId"
																 class="sticky-header-table small-scrolling-table"
																 flat
																 hide-bottom
																 dense
														>
															<template v-slot:body-cell-toggle="cell">
																<q-td>
																	<q-checkbox :value="indexInVirtualLog(cell.row) > -1" @input="toggleFromVirtualLog(cell.row, selectedLog)"/>
																</q-td>
															</template>
														</q-table>

													</q-td>
												</q-tr>
											</template>
										</q-table>

										<q-separator spaced/>

										<q-table :columns="virtualLogColumns"
												 :data="virtualLog.bundles"
												 :pagination.sync="virtualLogTablePagination"
												 class="sticky-header-table small-scrolling-table"
												 row-key="intBundleId"
												 flat
												 hide-bottom
												 dense
										>
											<template v-slot:top-left>
												<div class="q-table__title table-title">
													{{ $t('virtual.virtualBlock') }}
												</div>
											</template>
											<template v-slot:body-cell-actions="cell">
												<q-td>
													<q-btn @click="removeBundle(cell.row)"
														   icon="delete"
														   flat
													/>
													<q-btn @click="moveBundleUp(cell.row)"
														   icon="arrow_upward"
														   flat
													/>
													<q-btn @click="moveBundleDown(cell.row)"
														   icon="arrow_downward"
														   flat
													/>
												</q-td>
											</template>
										</q-table>

									</q-tab-panel>

									<q-tab-panel name="images">
										<div class="log-selector-scroll q-mt-md">
											<log-teaser v-for="log in virtualLog.logs"
														:key="log.logId"
														:log="log"
														class="log-selector q-mr-md"
														:class="{ selected : selectedLog === log }"
														@click.native="selectLog(log)"
											>
												<q-btn @click.stop="setTeaser(log.teaser || log.strPreviewImageURL)"
													   class="absolute-top-right q-mr-md teaser-selector"
													   :class="teaserSelectorClass(log)"
													   icon="photo"
													   flat dense
												>
													<q-tooltip>{{ $t('tooltip.asPreviewImage') }}</q-tooltip>
												</q-btn>
											</log-teaser>
										</div>
										<template v-if="selectedLog">
											<q-separator spaced/>
											<log-images-picker
													:props="logImagesPickerProps"
													:virtual-log="virtualLog"
													v-model="imageUrls"
													@change-teaser="setTeaser"
													emit reverse large virtual
											/>
										</template>
									</q-tab-panel>

									<q-tab-panel name="logInfo">
										<log-property-form :log="virtualLog" @update="updateLogPropsAndSave"/>
									</q-tab-panel>
								</q-tab-panels>
							</template>
						</template>
					</q-card-section>
				</template>
			</q-card>
		</q-dialog>
	</div>
</template>

<script>
	import {mapGetters} from "vuex";
	import VeneerLog from "@/components/common/VeneerLog";
	import LogTeaser from "@/components/common/element/LogTeaser";
	import BundleViewer from "@/components/detail/BundleViewer";
  import {
    ADD_BUNDLE_TO_VIRTUAL_LOG,
    MOVE_BUNDLE_TO_INDEX_IN_VIRTUAL_LOG,
    REMOVE_BUNDLE_FROM_VIRTUAL_LOG,
    REMOVE_FROM_VIRTUAL_LOG, SET_HEADER_REVEAL,
    SET_VIRTUAL_LOG, SET_VIRTUAL_LOG_EDIT_MODE,
    SET_VIRTUAL_LOG_PROPS,
    SET_VIRTUAL_LOG_TEASER
  } from "@/store/mutations.type";
	import {
		FETCH_BUNDLES_FOR_LOG,
		FETCH_LOG,
		FETCH_WATCHLIST,
		LOAD_LOG_FOR_VIRTUAL_LOG,
		UPDATE_VIRTUAL_LOG, UPDATE_WATCHLIST_ITEM
	} from "@/store/actions.type";
	import MeasurementList from "@/components/dialog/MeasurementList";
	import LogPropertyForm from "@/components/common/LogPropertyForm";
	import LogImagesPicker from "@/components/dialog/LogImagesPicker";
	import NumberFormattingFilters from "@/mixins/NumberFormattingFilters";
	import _cloneDeep from "lodash/cloneDeep";
  import debounce from "debounce";

	export default {
		name: "VirtualLogBuilder",
		mixins: [NumberFormattingFilters],
		components: {LogImagesPicker, LogPropertyForm, MeasurementList, BundleViewer, LogTeaser, VeneerLog},
		data() {
			return {
				selectedLog: null,
				loadingBundles: false,
				removingLog: false,
				loading: false,
				updating: false,
				tab: 'bundles',
				imageUrls: [],
				logPropertyFormErrors: {},
				showBundleImages: false,
				logTableColumns: [
					{name: 'logNo', label: this.$t('logNo'), field: 'logNo', align: 'left'},
					{name: 'species', label: this.$t('species'), field: row => row.strSpecieName || row.species.name, align: 'left'},
					{name: 'actions', align: 'right'}
				],
				bundleTableColumns: [
					{name: 'bundleNo', label: this.$t('bundle.packageNr'), field: 'strBundleNo', align: 'left'},
					{name: 'sheets', label: this.$t('bundle.sheets'), field: 'intSheets', align: 'left'},
					{name: 'length', label: this.$t('bundle.length'), field: 'intLength', align: 'left'},
					{name: 'width', label: this.$t('bundle.width'), field: 'intWidth', align: 'left'},
					{name: 'area', label: this.$t('bundle.area'), field: 'decArea', align: 'left'},
					{name: 'toggle', align: 'right'},
				],
				virtualLogColumns: [
					{name: 'bundleNo', label: this.$t('bundle.packageNr'), field: 'strBundleNo', align: 'left'},
					{name: 'sheets', label: this.$t('bundle.sheets'), field: 'intSheets', align: 'left'},
					{name: 'length', label: this.$t('bundle.length'), field: 'intLength', align: 'left'},
					{name: 'width', label: this.$t('bundle.width'), field: 'intWidth', align: 'left'},
					{name: 'area', label: this.$t('bundle.area'), field: 'decArea', align: 'left'},
					{name: 'actions', align: 'right'},
					{name: 'originallogNo', label: this.$t('bundle.originalLog'), field: 'logNo', align: 'left'},
					{name: 'originalBundleNo', label: this.$t('bundle.originalBundleNo'), field: 'originalBundleNo', align: 'left'}
				],
				bundleTablePagination: {
					page: 1,
					rowsPerPage: 0
				},
				virtualLogTablePagination: {
					page: 1,
					rowsPerPage: 0
				}
			};
		},
		computed: {
			...mapGetters(['virtualLog', 'watchlist', 'virtualLogWatchlistItem', 'editMode']),
			logImagesPickerProps() {
				return {
					item: {
						excludedImages: this.imageUrls,
						veneer: this.selectedLog
					}
				}
			},
		},
    created() {
      this.debouncedUpdate = debounce(this.updateLog, 800);
    },
    watch: {
			virtualLog(newLog, oldLog) {
				if (this.virtualLog && (!oldLog || oldLog.uid !== newLog.uid)) {
					this.show();
					this.tab = 'bundles';
					this.selectedLog = null;
					this.imageUrls = [];

					if (this.virtualLog.logsMeta) {
						this.loading = true;

						const logIds = this.virtualLog.logsMeta.map(logMeta => {
							return logMeta.logId;
						});

						Promise.all(logIds.map(identifier => this.loadLog(identifier))).then(() => {
							this.loading = false;
						});
					}

					this.imageUrls = this.virtualLog.imageUrls && this.virtualLog.imageUrls.length ?
						JSON.parse(this.virtualLog.imageUrls).map(image => image.strImageURL) : [];
				}
			},
			virtualLogWatchlistItem(newItem, oldItem) {
				this.showBundleImages = newItem.showBundleImages;
			},
      imageUrls(newUrls, oldUrls) {
        if(newUrls.length !== 0 && oldUrls.length !== 0) {
          this.debouncedUpdate();
        }
      }
		},
		methods: {
			show() {
				this.$store.commit(SET_VIRTUAL_LOG_EDIT_MODE, true);
			},
			hide() {
				this.$store.commit(SET_VIRTUAL_LOG_EDIT_MODE, false);
			},
			close() {
				this.hide();
				this.$store.commit(SET_VIRTUAL_LOG, null);
			},
      toggleBundleImages(value, e) {
        this.showBundleImages = value;
        this.debouncedUpdate();
      },
			loadLog(identifier) {
				return this.$store.dispatch(LOAD_LOG_FOR_VIRTUAL_LOG, identifier);
			},
			async updateLog() {
				this.updating = true;

				if(!this.virtualLog.species || !this.virtualLog.name) {
					this.updating = false;
					this.logPropertyFormErrors = {
						species: !this.virtualLog.species,
						name: !this.virtualLog.name
					};
					return;
				}

				const newVirtualLog = !this.virtualLog.hasOwnProperty('uid');

				await this.$store.dispatch(UPDATE_VIRTUAL_LOG, {
					log: this.virtualLog,
					logs: this.virtualLog.logs || [],
					watchlist: this.watchlist,
					imageUrls: this.imageUrls
				});

				if (newVirtualLog) {
					await this.$store.dispatch(FETCH_WATCHLIST, {watchlist: this.watchlist.uid});
				}

				if(this.virtualLogWatchlistItem && this.showBundleImages !== this.virtualLogWatchlistItem.showBundleImages) {
					await this.$store.dispatch(UPDATE_WATCHLIST_ITEM, {
						object: this.virtualLogWatchlistItem,
						watchlist: this.watchlist,
						props: {
							showBundleImages: this.showBundleImages
						},
            hideSuccess: true
					})
				}

				this.updating = false;
			},
			updateLogProps(updatedLogProps) {
				updatedLogProps.logNo = updatedLogProps.name;
				this.$store.commit(SET_VIRTUAL_LOG_PROPS, updatedLogProps);
			},
      updateLogPropsAndSave(updatedLogProps) {
         this.updateLogProps(updatedLogProps);
         this.debouncedUpdate();
      },
			async selectLog(log) {
				if(this.selectedLog === log || (this.selectedLog && this.selectedLog.logId === log.intLogId)) {
					this.selectedLog = null;
					return;
				}

				this.selectedLog = await this.loadBundles(log);
			},
			async loadBundles(log) {
				if(!log.uid) {
					this.loadingBundles = true;
					log = await this.$store.dispatch(FETCH_LOG, log.intLogId);
				}

				if (!log.bundles || !log.bundles.length) {
					this.loadingBundles = true;
					log.bundles = await this.$store.dispatch(FETCH_BUNDLES_FOR_LOG, log);
				}

				this.loadingBundles = false;
				return log;
			},
			async removeLog(log) {
				this.$q.dialog({
					title: this.$t('virtual.removeLog'),
					message: this.$t('confirmationText.removeLogFromVirtual'),
					cancel: true,
					persistent: true
				}).onOk(async () => {
					if (!log.bundles || !log.bundles.length) {
						this.removingLog = true;
						log.bundles = await this.$store.dispatch(FETCH_BUNDLES_FOR_LOG, log);
						this.removingLog = false;
					}

					// remove selected images of this log from virtual log
					const logImages = log.hasOwnProperty('lstImageURLs') ? log.lstImageURLs : JSON.parse(log.imageUrls),
						logImageUrls = logImages.map(logImage => logImage.strImageURL);
					this.imageUrls = this.imageUrls.filter(imageUrl => !logImageUrls.includes(imageUrl));

					if (this.selectedLog === log) {
						this.selectedLog = null;
					}
					this.$store.commit(REMOVE_FROM_VIRTUAL_LOG, log);
          await this.updateLog();
				});
			},
			setTeaser(teaser) {
				this.$store.commit(SET_VIRTUAL_LOG_TEASER, teaser);
				this.updateLog();
			},
			indexInVirtualLog(bundle) {
				return this.virtualLog.bundles.findIndex(setBundle => setBundle.intBundleId === bundle.intBundleId);
			},
			bundleTableRowClick(evt, row) {
				this.toggleFromVirtualLog(row, this.selectedLog);
			},
			toggleFromVirtualLog(bundle, log) {
				this.indexInVirtualLog(bundle) > -1 ? this.removeBundle(bundle) : this.addBundle(bundle, log);
			},
			addBundle(bundle, log, persist = true) {
				const clonedBundle = _cloneDeep(bundle);

				this.$store.commit(ADD_BUNDLE_TO_VIRTUAL_LOG, {bundle: {...clonedBundle, logNo: log.logNo}});
        if(persist) {
          this.debouncedUpdate();
        }
			},
			async addAllBundles(log) {
				log = await this.loadBundles(log);
				log.bundles.forEach(bundle => {
					this.addBundle(bundle, log, false);
				});
        this.debouncedUpdate();
			},
			removeBundle(bundle) {
				this.$store.commit(REMOVE_BUNDLE_FROM_VIRTUAL_LOG, bundle);
        this.debouncedUpdate();
			},
			moveBundleUp(bundle) {
				this.$store.commit(MOVE_BUNDLE_TO_INDEX_IN_VIRTUAL_LOG, {
					bundle,
					index: Math.max(0, this.indexInVirtualLog(bundle) - 1)
				});
        this.debouncedUpdate();
			},
			moveBundleDown(bundle) {
				this.$store.commit(MOVE_BUNDLE_TO_INDEX_IN_VIRTUAL_LOG, {
					bundle,
					index: Math.min(this.virtualLog.bundles.length, this.indexInVirtualLog(bundle) + 1)
				});
        this.debouncedUpdate();
			},
			selectLogByBundle(bundle) {
				this.selectLog(
					this.virtualLog.logs.find(log => parseInt(log.logId || log.intLogId) === bundle.intLogId)
				);
			},
			addFromSearch() {
        this.$store.commit(SET_HEADER_REVEAL, false);
				this.$router.push({name: 'search'});
				this.hide();
			},
			addFromWatchlist() {
        this.$store.commit(SET_HEADER_REVEAL, false);
				this.$router.push({name: 'watchlists'});
				this.hide();
			},
			addedBundles(log) {
				return this.getLogMetaData(log).consumedBundles;
			},
			logBundleCountColor(log) {
				const metaData = this.getLogMetaData(log);
				if (metaData.consumedBundles === 0) {
					return 'red';
				}
				if (metaData.consumedBundles === metaData.bundleCount) {
					return 'green';
				}
				return 'yellow';
			},
			getLogMetaData(log) {
				return this.virtualLog.logsMeta.find(logMeta => logMeta.logId === (log.logId || log.intLogId));
			},
			isSelected(log) {
				const selectedLogId = this.selectedLog ? this.selectedLog.intLogId || this.selectedLog.logId : null,
					logId = log.intLogId || log.logId;

				return this.selectedLog && (
					(this.selectedLog.uid && log.uid && this.selectedLog.uid === log.uid) ||
					(selectedLogId && logId && selectedLogId === logId)
				);
			},
			teaserSelectorClass(log) {
				if(this.virtualLog.teaser === log.teaser || this.virtualLog.teaser === log.strPreviewImageURL) {
					return 'selected';
				}
				return '';
			}
		}
	}
</script>

<style lang="scss" scoped>
	table table {
		thead tr {
			display: block;
			width: 100%;
		}

		tbody {
			display: block;
			max-height: 300px;
			overflow: auto;
			width: 100%;
		}
	}

	.log-selector-tr {
		cursor: pointer;
	}

	.log-selector-scroll {
		overflow-x: auto;
		overflow-y: hidden;
		display: block;
		white-space: nowrap;

		.log-selector {
			display: inline-block;
			width: 400px;
			cursor: pointer;

			&:last-child {
				margin: 0;
			}

			&.selected {
				border: 1px solid black;
			}

			&:hover {
				.teaser-selector {
					opacity: 1;
				}
			}
		}
	}

	.active-virtuallog {
		width: 100%;
		max-width: 1590px;
		margin: 0 auto;
		height: 35px;
		background: #ffffff;
		color: var(--q-color-dark);
	}

	@media(max-width: 600px) {
		.q-tab-panel {
			padding-left: 0;
			padding-right: 0;
		}
	}
</style>